import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { type Piece, sortByCreationDate } from '@dhl/pieces'
import { getGetParcelsQueryKey, type GetParcelsParams } from '@dhl/receiver-parcel-api'
import { getLegacyOutgoingPieces, getLegacyOutgoingPiecesQueryKey } from './useGetLegacyOutgoingPieces.js'
import { useGetParcelOutgoingPieces } from './useGetParcelOutgoingPieces.js'

export const getInTransitOutgoingPieces = (pieces: Piece[]): Piece[] =>
  pieces
    .filter((p) => p.type === 'outgoing' && p.category !== 'DELIVERED')
    .sort(sortByCreationDate)
    .reverse()

export const getDeliveredOutgoingPieces = (pieces: Piece[]): Piece[] =>
  pieces.filter((p) => p.type === 'outgoing' && p.category === 'DELIVERED').sort(sortByCreationDate)

export const getOutgoingPiecesQueryKey = (): readonly [
  readonly ['api/orders/sentShipments'],
  readonly ['/parcels', ...GetParcelsParams[]],
] => [getLegacyOutgoingPiecesQueryKey(), getGetParcelsQueryKey({ tab: 'outgoing' })] as const

export const useGetOutgoingPieces = (): UseQueryResult<{
  inTransit: Piece[]
  delivered: Piece[]
  parcels: Piece[]
}> => {
  const getOutgoingPieces = useGetParcelOutgoingPieces()

  return useQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: [...getOutgoingPiecesQueryKey(), getOutgoingPieces],
    queryFn: async () => Promise.allSettled([getLegacyOutgoingPieces(), getOutgoingPieces()]),
    select: (tasks) => {
      const pieces = tasks
        .filter((task) => task.status === 'fulfilled' && task.value)
        .flatMap((task) => (task as PromiseFulfilledResult<Piece[]>).value)
        .sort(sortByCreationDate)
        .reverse()

      const inTransit = getInTransitOutgoingPieces(pieces ?? [])
      const delivered = getDeliveredOutgoingPieces(pieces ?? [])

      return { inTransit, delivered, parcels: pieces }
    },
  })
}
